// Predicate / GROUP BY / ORDER BY / LIMIT parsers for the SELECT extractor.
// Predicate fragments are kept opaque (text + range) per SRE; v1 splits WHERE
// / HAVING on top-level AND and treats each conjunct as one BooleanFragment.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Query Structure Extractor — SELECT.

import type { IdentifierRef, ParseOptions, SqlRange } from '../../sql-grammar/model';
import { rangeFromOffsets, type LineIndex } from '../../sql-grammar/document/lineIndex';
import { readIdentifier, foldIdentifier } from '../scope/identifiers';
import { skipContextFor, trySkipNonCode, type SkipContext } from '../classify/tokenSkip';
import type { BooleanFragment, OrderByItem, LimitSpec } from '../types';

import type { SelectExtractCtx } from './extractQueryStructure.select';
import { parseComparisonFragment } from './parseComparison';
import type { TopKeyword } from './scanner';
import { findTopLevelCommas, scanTopLevelKeywords, trimRange } from './scanner';

// A predicate boundary trim that mirrors `trimRange` for whitespace + comments
// but does NOT skip a leading string literal or quoted identifier — so a
// predicate like `"col" = 1` keeps its column in the fragment text. (trimRange's
// leading loop skips ANY non-code region via trySkipNonCode, dropping the column.)
// Used by both WHERE parsers; the trailing loop is whitespace-only, identical to
// trimRange, so behaviour is byte-identical for predicates that do not start with
// a string/quoted identifier.
function isCommentStart(source: string, i: number, ctx: SkipContext): boolean {
  const c = source[i];
  const c2 = source[i + 1] ?? '';
  return (
    (c === '-' && c2 === '-') ||
    (c === '/' && c2 === '*') ||
    (ctx.supportsHashComment && c === '#')
  );
}

export function trimPredicateBoundary(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
): { startOffset: number; endOffset: number } {
  const ctx = skipContextFor(options);
  let s = start;
  while (s < end) {
    const c = source[s];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
      s++;
      continue;
    }
    if (isCommentStart(source, s, ctx)) {
      const skipped = trySkipNonCode(source, s, end, ctx);
      if (skipped > s) {
        s = skipped;
        continue;
      }
    }
    break;
  }
  let e = end;
  while (e > s) {
    const c = source[e - 1];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
      e--;
      continue;
    }
    break;
  }
  return { startOffset: s, endOffset: e };
}

// Build a BooleanFragment from a tight range, attaching `comparison` when the
// predicate is a recognisable binary/unary comparison (JCS-C1). Used by both
// the WHERE/HAVING splitter and the JOIN-ON inliner so every BooleanFragment
// in the AST flows through the same classifier.
export function buildBooleanFragment(
  source: string,
  options: ParseOptions,
  lineIdx: LineIndex,
  startOffset: number,
  endOffset: number,
): BooleanFragment {
  const comparison = parseComparisonFragment(source, startOffset, endOffset, options, lineIdx);
  return {
    range: rangeFromOffsets(lineIdx, startOffset, endOffset),
    text: source.slice(startOffset, endOffset),
    ...(comparison ? { comparison } : {}),
  };
}

export function parseBooleanFragments(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): readonly BooleanFragment[] | undefined {
  const { source, options, lineIdx } = ctx;
  const trimmed = trimPredicateBoundary(source, start, end, options);
  if (trimmed.startOffset >= trimmed.endOffset) return undefined;
  // Split on top-level `AND` and `OR` keywords. The `AND` that belongs to a
  // `BETWEEN lo AND hi` is part of that operator's own syntax, not a boundary:
  // a top-level `BETWEEN` arms a "pending between" flag; the very next top-level
  // `AND` is consumed by that BETWEEN (not a split) and disarms the flag; later
  // `AND`s split normally. `OR` always splits. Sequential BETWEENs each claim
  // their own following `AND` in turn. Paren depth is already handled by
  // scanTopLevelKeywords (a nested BETWEEN/AND/OR lives at depth > 0 and never
  // appears here, so a parenthesised `(a OR b)` group stays one fragment).
  const kws = scanTopLevelKeywords(source, trimmed.startOffset, trimmed.endOffset, options);
  const splits: { at: number; after: number; connector: 'AND' | 'OR' }[] = [];
  let pendingBetween = false;
  for (const k of kws) {
    if (k.upper === 'BETWEEN') {
      pendingBetween = true;
      continue;
    }
    if (k.upper === 'AND') {
      if (pendingBetween) {
        // This AND is the `BETWEEN lo AND hi` separator — skip it as a split.
        pendingBetween = false;
        continue;
      }
      splits.push({ at: k.start, after: k.end, connector: 'AND' });
    } else if (k.upper === 'OR') {
      splits.push({ at: k.start, after: k.end, connector: 'OR' });
    }
  }
  const out: BooleanFragment[] = [];
  const push = (a: number, b: number, connector: 'AND' | 'OR' | undefined): void => {
    const t = trimPredicateBoundary(source, a, b, options);
    if (t.startOffset >= t.endOffset) return;
    out.push({
      ...buildBooleanFragment(source, options, lineIdx, t.startOffset, t.endOffset),
      ...(connector ? { leadingConnector: connector } : {}),
    });
  };
  let s = trimmed.startOffset;
  // The first fragment has no leading connector; each later fragment is led by
  // the connector of the split that precedes it.
  let connector: 'AND' | 'OR' | undefined;
  for (const sp of splits) {
    push(s, sp.at, connector);
    s = sp.after; // skip past the AND (3) / OR (2) keyword via its scanned end
    connector = sp.connector;
  }
  push(s, trimmed.endOffset, connector);
  return out.length ? out : undefined;
}

export function parseGroupByItems(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): readonly IdentifierRef[] {
  const { source, options, lineIdx } = ctx;
  const trimmed = trimRange(source, start, end, options);
  if (trimmed.startOffset >= trimmed.endOffset) return [];
  const commas = findTopLevelCommas(source, trimmed.startOffset, trimmed.endOffset, options);
  const out: IdentifierRef[] = [];
  let s = trimmed.startOffset;
  const push = (a: number, b: number): void => {
    const t = trimRange(source, a, b, options);
    if (t.startOffset >= t.endOffset) return;
    out.push(buildSingleRef(source, options, lineIdx, t.startOffset, t.endOffset));
  };
  for (const c of commas) { push(s, c); s = c + 1; }
  push(s, trimmed.endOffset);
  return out;
}

export function parseOrderByItems(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): readonly OrderByItem[] {
  const { source, options, lineIdx } = ctx;
  const trimmed = trimRange(source, start, end, options);
  if (trimmed.startOffset >= trimmed.endOffset) return [];
  const commas = findTopLevelCommas(source, trimmed.startOffset, trimmed.endOffset, options);
  const out: OrderByItem[] = [];
  let s = trimmed.startOffset;
  const push = (a: number, b: number): void => {
    const t = trimRange(source, a, b, options);
    if (t.startOffset >= t.endOffset) return;
    out.push(buildOrderByItem(source, lineIdx, t.startOffset, t.endOffset));
  };
  for (const c of commas) { push(s, c); s = c + 1; }
  push(s, trimmed.endOffset);
  return out;
}

// eslint-disable-next-line max-lines-per-function, complexity
export function parseLimitSpec(
  ctx: SelectExtractCtx,
  limitKw: TopKeyword,
  startInclusive: number,
  end: number,
): LimitSpec | undefined {
  const { source, options, lineIdx } = ctx;
  // The clause may be one of:
  //   LIMIT n [OFFSET m]
  //   LIMIT m, n (MySQL/SQLite legacy)
  //   OFFSET m [FETCH NEXT n ROWS ONLY]
  //   OFFSET m [LIMIT n]
  //   FETCH FIRST n ROWS ONLY (Oracle/ANSI)
  let count: string | undefined;
  let offset: string | undefined;
  const kws = scanTopLevelKeywords(source, limitKw.start, end, options);
  for (let k = 0; k < kws.length; k++) {
    const u = kws[k].upper;
    if (u === 'LIMIT') {
      const after = kws[k].end;
      const next = nextKeywordStart(kws, k + 1, end);
      const text = source.slice(after, next).trim();
      // MySQL/SQLite `LIMIT m, n` form: m=offset, n=count.
      const comma = text.indexOf(',');
      if (comma >= 0) {
        offset = text.slice(0, comma).trim();
        count = text.slice(comma + 1).trim();
      } else {
        count = text;
      }
    } else if (u === 'OFFSET') {
      const after = kws[k].end;
      const next = nextKeywordStart(kws, k + 1, end);
      const text = source.slice(after, next).trim();
      // Strip trailing ROW / ROWS keywords.
      offset = text.replace(/\s+ROWS?\s*$/i, '').trim();
    } else if (u === 'FETCH') {
      // FETCH FIRST n ROWS ONLY / FETCH NEXT n ROWS ONLY
      const after = kws[k].end;
      const next = nextKeywordStart(kws, k + 1, end);
      const text = source.slice(after, next);
      const m = text.match(/^\s*(?:FIRST|NEXT)\s+(\S+)/i);
      if (m) count = m[1].trim();
    }
  }
  void startInclusive;
  if (count === undefined && offset === undefined) return undefined;
  return {
    range: rangeFromOffsets(lineIdx, limitKw.start, end),
    ...(count ? { count } : {}),
    ...(offset ? { offset } : {}),
  };
}

function nextKeywordStart(kws: readonly TopKeyword[], from: number, fallbackEnd: number): number {
  for (let k = from; k < kws.length; k++) {
    if (kws[k].upper === 'OFFSET' || kws[k].upper === 'LIMIT' || kws[k].upper === 'FETCH') {
      return kws[k].start;
    }
  }
  return fallbackEnd;
}

function buildSingleRef(
  source: string,
  options: ParseOptions,
  lineIdx: LineIndex,
  start: number,
  end: number,
): IdentifierRef {
  const text = source.slice(start, end).trim();
  const first = readIdentifier(source, start, end, options);
  if (first) {
    const parts = [first];
    let pos = first.end;
    while (pos < end && source[pos] === '.') {
      const next = readIdentifier(source, pos + 1, end, options);
      if (!next) break;
      parts.push(next);
      pos = next.end;
    }
    // A column ref only when the dotted chain consumed the whole trimmed text;
    // anything else (function call, positional ordinal) keeps the full-text
    // fallback below so `qualified.raw` is never a truncated fragment.
    if (source.slice(pos, end).trim().length === 0) {
      const last = parts[parts.length - 1];
      const schema = parts.length >= 2 ? parts[parts.length - 2] : undefined;
      const database = parts.length >= 3 ? parts[parts.length - 3] : undefined;
      return {
        qualified: {
          name: last.value,
          raw: source.slice(start, pos),
          quoted: parts.some((p) => p.quoted),
          foldedName: foldIdentifier(last.value, last.quoted, options),
          ...(schema ? { schema: schema.value, foldedSchema: foldIdentifier(schema.value, schema.quoted, options) } : {}),
          ...(database ? { database: database.value, foldedDatabase: foldIdentifier(database.value, database.quoted, options) } : {}),
        },
        range: rangeFromOffsets(lineIdx, start, end),
        kind: 'column',
      };
    }
  }
  return {
    qualified: { name: text, raw: text, quoted: false, foldedName: foldIdentifier(text, false, options) },
    range: rangeFromOffsets(lineIdx, start, end),
    kind: 'column',
  };
}

function buildOrderByItem(
  source: string,
  lineIdx: LineIndex,
  start: number,
  end: number,
): OrderByItem {
  const range: SqlRange = rangeFromOffsets(lineIdx, start, end);
  const text = source.slice(start, end);
  let nulls: 'first' | 'last' | undefined;
  let trimmedText = text;
  const nullsMatch = text.match(/\s+NULLS\s+(FIRST|LAST)\s*$/i);
  if (nullsMatch) {
    nulls = nullsMatch[1].toUpperCase() === 'FIRST' ? 'first' : 'last';
    trimmedText = text.slice(0, nullsMatch.index).trimEnd();
  }
  const dirMatch = trimmedText.match(/\s+(ASC|DESC)\s*$/i);
  let direction: 'asc' | 'desc' | 'default' = 'default';
  let exprText = trimmedText.trim();
  if (dirMatch) {
    direction = dirMatch[1].toUpperCase() === 'ASC' ? 'asc' : 'desc';
    exprText = trimmedText.slice(0, dirMatch.index).trim();
  }
  return {
    range, expressionText: exprText, direction,
    ...(nulls !== undefined ? { nulls } : {}),
  };
}

// Re-exported so tests / debug paths can use the same skip primitive.
export { trySkipNonCode, skipContextFor };
